import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useDeepLinkContext } from "@/hooks/tauri/DeepLinkProvider";
import { useDeepLinksListener } from "@/hooks/tauri/useDeepLinksListener";
import { useGrantCameraAccess } from "@/hooks/tauri/useGrantCameraAccess";
import { useFetchPatient } from "@/infra/apis/patients/useFetchPatient";
import { useFetchActiveTreatment } from "@/infra/apis/treatments/useFetchActiveTreatment";
import { useInitializeIntl } from "@/infra/locale";
import { analyticsService } from "@/infra/observability/analyticsService";
import { LOCALE_GET_PARAM } from "@/infra/router/utils";
import {
  AvailableLocales,
  DEFAULT_LOCALE,
  type AppLocale,
  errorTrackingService,
  useFetchTermsAndConditionsStatus,
  useNavigateToTermsAndConditions,
} from "@akina/infra";
import { useCmsCopyStore, useLocaleStore } from "@akina/presentation";
import { useTrackAppClosed } from "../analytics/useTrackAppClosed";
import { useInterceptMediaCommands } from "./useInterceptMediaCommands";
import { usePreventNavigationAndReload } from "./usePreventUserReload";

export const useInitializeApp = () => {
  const { locale, setLocale } = useLocaleStore();
  const { cmsCopy } = useCmsCopyStore();
  const [searchParams] = useSearchParams();
  const localeFromUrl = searchParams.get(LOCALE_GET_PARAM);
  const location = useLocation();
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyPolicyModalOpen, setPrivacyPolicyModalOpen] = useState(false);
  const [termsAndPrivacyPolicyModalOpen, setTermsAndPrivacyPolicyModalOpen] = useState(false);
  const { isDeepLinkHandled } = useDeepLinkContext();
  const { data: patientTerms } = useFetchTermsAndConditionsStatus(isDeepLinkHandled);
  const { data: activeTreatment } = useFetchActiveTreatment(isDeepLinkHandled);
  const {
    isLoading,
    fetchStatus,
    data: patient,
  } = useFetchPatient({
    throwOnError: false,
    isDeepLinkHandled,
  });

  const { isLocaleDataLoading } = useInitializeIntl();

  useGrantCameraAccess();
  usePreventNavigationAndReload();
  useTrackAppClosed();
  useInterceptMediaCommands();

  useEffect(() => {
    if (patient?.preferences?.language && patient?.preferences?.language !== locale) {
      setLocale(patient?.preferences?.language);
    }
  }, [setLocale, patient?.preferences?.language]);

  useDeepLinksListener();

  const isLoadingData = isLoading && fetchStatus !== "idle";
  const isInitializing = !isDeepLinkHandled || isLoadingData || isLocaleDataLoading;

  useNavigateToTermsAndConditions({
    terms: patientTerms,
    setTermsModalOpen,
    setPrivacyPolicyModalOpen,
    setTermsAndPrivacyPolicyModalOpen,
    isDeepLinkHandled,
  });

  useEffect(() => {
    if (patient) {
      errorTrackingService.identifyUser({
        id: patient.patientId,
      });
      analyticsService.identifyUser({
        id: patient.patientId,
      });
    }
  }, [patient]);

  useEffect(() => {
    if (!isDeepLinkHandled) return;
    if (localeFromUrl) {
      const locale = AvailableLocales.includes(localeFromUrl as AppLocale)
        ? (localeFromUrl as AppLocale)
        : DEFAULT_LOCALE;
      return setLocale(locale);
    }
    return setLocale(locale);
  }, [localeFromUrl, isDeepLinkHandled]);

  useEffect(() => {
    if (location.pathname) {
      analyticsService.page({ path: location.pathname });
    }
  }, [location]);

  return {
    isInitializing,
    activeTreatment,
    patientTerms,
    termsModalOpen,
    setTermsModalOpen,
    privacyPolicyModalOpen,
    setPrivacyPolicyModalOpen,
    termsAndPrivacyPolicyModalOpen,
    setTermsAndPrivacyPolicyModalOpen,
    cmsCopy,
  };
};
